import { Button, Card, Col, Container, Row, Stack } from "react-bootstrap";
import { DynamicText } from "@/ui/DynamicText";
import JobSearch from "@sourceflow-uk/job-search";
import "@sourceflow-uk/job-search/dist/bundle.css";
import { trimText } from "@/functions/trimText";
import { useLocale } from "@/hooks/useLocale";
import { placeholders } from "@/data/placeholders";
import classes from "./styles.module.scss";
import clsx from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLink } from "@/hooks/useLink";
import Reset from "@/assets/Reset.svg";
import jobsType from "@/../.sourceflow/jobs-Type.json";
import jobsSpecialisms from "@/../.sourceflow/jobs-Specialisms.json";
import jobsDepartment from "@/../.sourceflow/jobs-Department.json";
import jobsOfficeLocations from "@/../.sourceflow/jobs-Office Locations.json";
import jobsLocation from "@/../.sourceflow/jobs-Location.json";
import sourceflowEnums from "@/sourceflow.enums";
import PropTypes from "prop-types";
import CaretDown from "@/assets/CaretDown.svg";

export const JobsPageFeedSection = ({ className, prefilters, jobsUrl, exclude, pathPrefix, variant }) => {
  const [seconds, setSeconds] = useState(5);
  const locale = useLocale();
  const link = useLink();
  const insertRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [allowRedirect, setAllowRedirect] = useState(true);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);

      if (params.has("redirect") && params.get("redirect") === "0") {
        setAllowRedirect(false);
      }
    }
  }, []);

  useEffect(() => {
    const wrapper = document.getElementsByClassName("js-form-input-wrapper").item(0);
    if (wrapper && insertRef.current === false) {
      const input1 = wrapper.children[0];
      const input2 = wrapper.children[1];
      const input3 = wrapper.children[2];
      const button = wrapper.children[3];
      const div1 = document.createElement("div");
      const div2 = document.createElement("div");
      const div3 = document.createElement("div");
      const label1 = document.createElement("label");
      const label2 = document.createElement("label");
      const label3 = document.createElement("label");
      label1.innerText = placeholders[`${pathPrefix}.query.label.${locale}`];
      label2.innerText = placeholders[`${pathPrefix}.location.label.${locale}`];
      label3.innerText = placeholders[`${pathPrefix}.radius.label.${locale}`];
      div1.append(label1, input1);
      div2.append(label2, input2);
      div3.append(label3, input3);
      wrapper.replaceChildren(div1, div2, div3, button);
      insertRef.current = true;
    }
  }, [locale]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setReady(true);
    }, 1500);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const redirectAfterDelay = useCallback(() => {
    const interval = setTimeout(() => {
      if (allowRedirect === true) {
        if (seconds > 0) {
          setSeconds(seconds - 1);
        } else {
          const urlParams = new URLSearchParams({ redirect: 0 });
          window.location.href = `${window.location.pathname}?${urlParams.toString()}`;
        }
      }
    }, 1000);

    return () => {
      clearTimeout(interval);
    };
  }, [seconds, allowRedirect]);

  return (
    <div className={clsx(className, classes.root, { loading: !ready })} data-variant={variant}>
      <Container className="px-2 px-md-3">
        <JobSearch
          translations={{
            "button.text": placeholders[`${pathPrefix}.button.${locale}`],
            "input.placeHolder": placeholders[`${pathPrefix}.query.placeholder.${locale}`],
            "input.locationPlaceholder": placeholders[`${pathPrefix}.location.placeholder.${locale}`],
            "searchResults.noResultsText": placeholders[`${pathPrefix}.noResults.${locale}`],
            "filters.resetSearchText": (
              <>
                <Reset />
                <span className="ms-2">{placeholders[`${pathPrefix}.resetSearch.${locale}`]}</span>
              </>
            ),
          }}
          options={{
            prefilters,
            // searchPagination: {
            //   variant: "paged",
            //   reactPaginationOptions: {
            //     pageRangeDisplayed: 3,
            //     marginPagesDisplayed: 4,
            //     nextLabel: "next >",
            //     previousLabel: "< prev",
            //   },
            // },
            urlFilters: {
              mode: "active",
              jobCategories: [jobsLocation, jobsOfficeLocations, jobsType, jobsSpecialisms, jobsDepartment],
              pushToHistoryOnSearch: true,
              changeURLOnFilterChange: true,
            },
            jobsUrl,
            searchInput: {
              queryInput: true,
              locationInput: true,
              locationRegion: "", 
              radiusInput: true,
              radiusOptions: {
                units: "kilometres",
              },
            },
            searchFiltersOptions: {
              filterFoldElement: <CaretDown width={20} height={20} />,
              showResetSearchButton: true,
              exclude,
              topLevelCategoryOrder: [jobsLocation.id, jobsOfficeLocations.id],
              categorySort: {
                [jobsLocation.id]: function (a, b) {
                  const order = [
                    sourceflowEnums.categories.locations.uk,
                    sourceflowEnums.categories.locations.de,
                    sourceflowEnums.categories.locations.nl,
                    sourceflowEnums.categories.locations.hk,
                    sourceflowEnums.categories.locations.sg,
                  ];

                  return order.indexOf(a.id) > order.indexOf(b.id) ? 1 : -1;
                },
              },
              nestedCategories: [jobsOfficeLocations],
            },
            searchResults: ({ results }) => {
              if (!results || results.length === 0) {
                redirectAfterDelay();

                return (
                  <Row>
                    <div className="js-result-none">
                      {allowRedirect
                        ? placeholders[`${pathPrefix}.noResults.withRedirect.${locale}`].replaceAll(
                            "{seconds}",
                            seconds
                          )
                        : placeholders[`${pathPrefix}.noResults.${locale}`]}
                    </div>
                  </Row>
                );
              }

              return (
                <Row>
                  {results.map(({ job }, k) => {
                    const isFreelance = JSON.stringify(job.categories).includes(
                      sourceflowEnums.categories.type.contract
                    );

                    const externalSite = job.categories.find((c) => c.name == "Location")?.values?.[0]?.name;

                    let jobUrl = link("jobs.item", job.url_slug);

                    if (variant === "external") {
                      switch (externalSite) {
                        case "UK":
                          jobUrl = `https://www.gravitasgroup.co.uk/jobs/${job.url_slug}`;
                          break;
                        case "Germany":
                          jobUrl = `https://www.gravitasgroup.de/jobs/${job.url_slug}`;
                          break;
                        case "Singapore":
                          jobUrl = `https://www.gravitasgroup.com.sg/jobs/${job.url_slug}`;
                          break;
                        case "Netherlands":
                          jobUrl = `https://www.gravitasgroup.nl/vacatures/${job.url_slug}`;
                          break;
                        case "Hong Kong and Mainland China":
                          jobUrl = `https://www.gravitasgroup.com.hk/jobs/${job.url_slug}`;
                      }
                    }

                    return (
                      <Col xs={12} key={job.id} className="mb-4">
                        <Card className={clsx(classes.card, "bg-light")}>
                          <Card.Header>
                            {k < 4 && <i>{placeholders[`job.card.new.${locale}`]}</i>}
                            <Card.Title className="mb-3" dangerouslySetInnerHTML={{ __html: job.title }} />
                            <Stack className="flex-md-row" gap={{ md: 5, xs: 2 }}>
                              <dl className="d-flex flex-row mb-0">
                                <dt className="me-2">
                                  {isFreelance ? "Rate" : <DynamicText path={`job.card.salary.label.${locale}`} />}:
                                </dt>
                                <dd className="mb-0" dangerouslySetInnerHTML={{ __html: job.salary_package }} />
                              </dl>
                              <dl className="d-flex flex-row mb-0">
                                <dt className="me-2">
                                  <DynamicText path={`job.card.location.label.${locale}`} />:
                                </dt>
                                <dd className="mb-0" dangerouslySetInnerHTML={{ __html: job.addresses[0] }} />
                              </dl>
                            </Stack>
                          </Card.Header>
                          <Card.Body>
                            <Card.Text dangerouslySetInnerHTML={{ __html: trimText(job.description, 148) }} />
                          </Card.Body>
                          <Card.Footer className="d-flex flex-row justify-content-between align-items-center">
                            <a href={`${jobUrl}#apply`} target={variant === "internal" ? "_self" : "_blank"}>
                              <DynamicText path={`job.card.apply.${locale}`} />
                            </a>
                            <Button
                              variant={variant === "internal" ? "outline-quinary" : "outline-primary"}
                              href={jobUrl}
                              target={variant === "internal" ? "_self" : "_blank"}
                            >
                              <DynamicText path={`job.card.button.${locale}`} />
                            </Button>
                          </Card.Footer>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              );
            },
          }}
        />
      </Container>
    </div>
  );
};

JobsPageFeedSection.defaultProps = {
  prefilters: null,
  jobsUrl: "/jobs",
  variant: "external",
  pathPrefix: "jobs.search",
};

JobsPageFeedSection.propTypes = {
  variant: PropTypes.oneOf(["internal", "external"]),
};
